/**
 * Mobile-internal search filters hook for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal copy of useSearchFilters logic.
 * Cluster A 9m verified canonical advancedFilters key naming:
 *   - minG/maxG (NOT minGames/maxGames)
 *   - hitterQualified/pitcherQualified (NOT mlbQualifiedHitters/mlbQualifiedPitchers)
 * Diverging from these keys breaks 9m's 11-filter bug class closure.
 *
 * 1:1 port of pages/league-dashboard/free-agent-search/hooks/useSearchFilters.js.
 * Pure state mgmt — no API calls, no localStorage, no URL sync.
 */

import { useState, useCallback } from 'react';

const INITIAL_ADVANCED_FILTERS = {
  // Hitter filters
  minAB: '',
  maxAB: '',
  minHR: '',
  maxHR: '',
  minG: '',
  maxG: '',
  minPrice: '',
  maxPrice: '',
  hitterQualified: false,

  // Pitcher filters
  minK: '',
  maxK: '',
  minGS: '',
  maxGS: '',
  minIP: '',
  maxIP: '',
  pitcherQualified: false,

  // Date filters
  dateRange: 'season',
  customStartDate: '',
  customEndDate: '',

  // MLB Qualification thresholds
  qualifiedPA: 502,
  qualifiedIP: 162,
};

export const useMobileSearchFilters = ({ includeNon40Man: initialNon40Man = false } = {}) => {
  // Search & Tab State
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('hitting');
  const [positionFilter, setPositionFilter] = useState('all');
  const [viewMode, setViewMode] = useState('free_agents');
  const [showAll, setShowAll] = useState(false);
  const [statsYear, setStatsYear] = useState('current');
  const [includeNon40Man, setIncludeNon40Man] = useState(initialNon40Man);

  // Rolling-row visibility toggle. Default ON preserves the legacy two-row UX.
  // Ephemeral — resets on page reload (no localStorage / URL sync yet).
  const [showRecent, setShowRecent] = useState(true);

  // Advanced Filters State (canonical post-9m keys — see top-of-file comment)
  const [advancedFilters, setAdvancedFilters] = useState({ ...INITIAL_ADVANCED_FILTERS });

  // Reset helper
  const resetFilters = useCallback(() => {
    setSearchTerm('');
    setPositionFilter('all');
    setAdvancedFilters({ ...INITIAL_ADVANCED_FILTERS });
  }, []);

  return {
    // State values
    searchTerm,
    activeTab,
    positionFilter,
    viewMode,
    showAll,
    advancedFilters,
    statsYear,
    includeNon40Man,
    showRecent,

    // Setters
    setSearchTerm,
    setActiveTab,
    setPositionFilter,
    setViewMode,
    setShowAll,
    setAdvancedFilters,
    setStatsYear,
    setIncludeNon40Man,
    setShowRecent,

    // Helpers
    resetFilters,
  };
};

export default useMobileSearchFilters;
